'use client';

import { usePathname } from 'next/navigation';
import JsonLd from './JsonLd';
import { SITE_URL } from '@/lib/siteUrl';

const SITE = SITE_URL;
const BRAND_NAME = 'Dr. Detoxer';
const SUPPORTED_LANGUAGE_CODES = [
  'en-IN',
  'hi-IN',
  'gu-IN',
  'mr-IN',
  'kn-IN',
  'ta-IN',
  'te-IN',
  'bn-IN',
];

const PAGE_COPY: Record<string, { name: string; description: string; type: string }> = {
  '/': {
    name: 'Dr. Detoxer — Natural Vegetable & Fruit Wash to Remove Pesticides',
    description:
      'Natural fruit and vegetable wash with Neem, Tulsi and Lemon. Removes pesticides, bacteria and dirt for safe daily family use.',
    type: 'WebPage',
  },
  '/home': {
    name: 'Dr. Detoxer Home',
    description:
      'Discover the Dr. Detoxer fruit and vegetable wash, product benefits, certification highlights and buying options.',
    type: 'WebPage',
  },
  '/about': {
    name: 'About Dr. Detoxer',
    description:
      'Learn how Dr. Detoxer was built, what goes into the formula and the quality standards behind the brand.',
    type: 'AboutPage',
  },
  '/contact': {
    name: 'Contact Dr. Detoxer',
    description:
      'Reach Dr. Detoxer by email, phone or WhatsApp for orders, support and partnerships.',
    type: 'ContactPage',
  },
  '/privacy': {
    name: 'Privacy Policy',
    description:
      'How Dr. Detoxer collects, uses and protects personal data on the website and during order fulfilment.',
    type: 'WebPage',
  },
  '/refund': {
    name: 'Refund & Return Policy',
    description:
      'Eligibility, timelines and process for returns and refunds at Dr. Detoxer.',
    type: 'WebPage',
  },
  '/shipping': {
    name: 'Shipping Policy',
    description:
      'Delivery timelines, shipping charges and order tracking information for Dr. Detoxer orders in India.',
    type: 'WebPage',
  },
  '/terms': {
    name: 'Terms of Service',
    description:
      'Terms and conditions governing website use and purchases from Dr. Detoxer.',
    type: 'WebPage',
  },
  '/home/track-order': {
    name: 'Track Your Dr. Detoxer Order',
    description:
      'Track orders by order number, phone number or email for Dr. Detoxer purchases.',
    type: 'WebPage',
  },
};

const certificationSignals = [
  {
    name: 'FSSAI quality compliance',
    description: 'Food-safety aligned manufacturing and packaging standards for produce washing.',
  },
  {
    name: 'GMP-certified facility',
    description: 'Manufacturing process aligned with good manufacturing practice standards.',
  },
  {
    name: 'ISO 2015 quality process',
    description: 'Structured quality management signals for production and testing.',
  },
  {
    name: 'Lab tested product',
    description: 'Independent laboratory testing for efficacy and safety claims.',
  },
];

export default function PageStructuredData() {
  const pathname = usePathname();
  const page = PAGE_COPY[pathname || '/'];

  if (!page) {
    return null;
  }

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': page.type,
    name: page.name,
    description: page.description,
    url: `${SITE}${pathname === '/' ? '' : pathname}`,
    inLanguage: SUPPORTED_LANGUAGE_CODES,
    isPartOf: {
      '@id': `${SITE}/#website`,
    },
    about: {
      '@id': `${SITE}/#organization`,
    },
    publisher: {
      '@id': `${SITE}/#organization`,
    },
  };

  const homeCertificationsSchema =
    pathname === '/' || pathname === '/home'
      ? {
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: `${BRAND_NAME} certifications and testing signals`,
          description:
            'Visible quality badges and testing signals shown on the Dr. Detoxer homepage.',
          itemListOrder: 'https://schema.org/ItemListOrderAscending',
          numberOfItems: certificationSignals.length,
          itemListElement: certificationSignals.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.name,
            description: item.description,
          })),
        }
      : null;

  return (
    <>
      <JsonLd data={pageSchema} id={`page-schema-${pathname?.replace(/\//g, '-') || 'home'}`} />
      {homeCertificationsSchema ? (
        <JsonLd data={homeCertificationsSchema} id="home-certifications-schema" />
      ) : null}
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'Brand',
          name: BRAND_NAME,
          url: SITE,
        }}
        id="brand-schema"
      />
    </>
  );
}